import _extends from "/usr/app/node_modules/@babel/runtime/helpers/esm/extends.js";
import _regenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/regenerator.js";
import _asyncToGenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _slicedToArray from "/usr/app/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import React from "react";
var __jsx = React.createElement;
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Input from "@/components/UiKit/Input/Input";
import Select from "@/components/UiKit/Select/Select";
import InputPhoneNumber from "@componentsShared/InputPhoneNumber/InputPhoneNumber";
import useApi from "@hooks/useApi";
import useBoolean from "@hooks/useBoolean";
import { useTranslation } from "@i18n";
import { useLang } from "@/common/Contexts/LanguageContext";
import { api__getCitiesDropDown } from "@/apis/assets/api__get_cities_drop_down";
import { trackEvents } from "@/common/events/events";
import submitFleetLead from "../../api/submitFleetLead";
import NumberStepper from "./NumberStepper";
import FleetResultModal from "../FleetResultModal/FleetResultModal";
import ReCaptcha from "./ReCaptcha";
import formImage from "../../assets/DesktopFormImage.png";
var FleetFormSchema = z.object({
  first_name: z.string().min(1, "fleet/name_required").min(2, "fleet/name_min").regex(/^[؀-ۿa-zA-Z\s]+$/, "fleet/name_invalid"),
  organization: z.string().min(1, "fleet/company_required"),
  mobile_no: z.string().min(1, "fleet/phone_required").regex(/^05\d{8}$/, "fleet/phone_invalid"),
  email: z.string().email("fleet/email_invalid").optional().or(z.literal("")),
  city_id: z.string().min(1, "fleet/city_required"),
  number_of_cars: z.preprocess(function (v) {
    return typeof v === "number" && Number.isNaN(v) ? undefined : v;
  }, z.number({
    required_error: "fleet/cars_required",
    invalid_type_error: "fleet/cars_required"
  })["int"]().min(1, "fleet/cars_min")),
  request: z.string().min(1, "fleet/description_required")
});
var FieldLabel = function FieldLabel(_ref) {
  var children = _ref.children,
    required = _ref.required;
  return /*#__PURE__*/React.createElement("label", {
    className: "block mb-2 text-sm font-medium text-[#1B2035]"
  }, children, required && /*#__PURE__*/React.createElement("span", {
    className: "text-[#E4002B]"
  }, " *"));
};
var FleetForm = function FleetForm() {
  var _useTranslation = useTranslation(["web_fleet"]),
    tShort = _useTranslation.tShort;
  var lang = useLang();
  var _useApi = useApi([], api__getCitiesDropDown),
    _useApi2 = _slicedToArray(_useApi, 3),
    cities = _useApi2[2];
  var submitting = useBoolean(false);
  var successModal = useBoolean(false);
  var failModal = useBoolean(false);

  // reCAPTCHA: token + error flag. `captchaKey` remounts the widget to reset it.
  var _useState = useState(null),
    captchaToken = _useState[0],
    setCaptchaToken = _useState[1];
  var captchaInvalid = useBoolean(false);
  var _useState2 = useState(0),
    captchaKey = _useState2[0],
    setCaptchaKey = _useState2[1];
  var _useForm = useForm({
      resolver: zodResolver(FleetFormSchema),
      mode: "onTouched",
      defaultValues: {
        first_name: "",
        organization: "",
        mobile_no: "",
        email: "",
        city_id: "",
        number_of_cars: 1,
        request: ""
      }
    }),
    control = _useForm.control,
    handleSubmit = _useForm.handleSubmit,
    reset = _useForm.reset,
    errors = _useForm.formState.errors;
  var resetCaptcha = function resetCaptcha() {
    setCaptchaToken(null);
    setCaptchaKey(function (k) {
      return k + 1;
    }); // force ReCaptcha remount -> fresh widget
  };
  var onSubmit = /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee(data) {
      var _cityList$find$name, _cityList$find;
      var _data$email, _t;
      return _regenerator().w(function (_context) {
        while (1) switch (_context.p = _context.n) {
          case 0:
            if (!submitting.value) {
              _context.n = 1;
              break;
            }
            return _context.a(2);
          case 1:
            if (captchaToken) {
              _context.n = 2;
              break;
            }
            captchaInvalid.setTrue();
            return _context.a(2);
          case 2:
            captchaInvalid.setFalse();
            submitting.setTrue();
            trackEvents("b2b_form_submit", {
              phone_number: data.mobile_no,
              number_of_cars: data.number_of_cars,
              city: (_cityList$find$name = (_cityList$find = cityList.find(function (c) {
                return String(c.id) === data.city_id;
              })) === null || _cityList$find === void 0 ? void 0 : _cityList$find.name) !== null && _cityList$find$name !== void 0 ? _cityList$find$name : data.city_id
            });
            _context.p = 3;
            _context.n = 4;
            return submitFleetLead({
              first_name: data.first_name,
              organization: data.organization,
              mobile_no: data.mobile_no,
              // 05XXXXXXXX — server normalizes to 00966…
              email: (_data$email = data.email) !== null && _data$email !== void 0 ? _data$email : "",
              request: data.request,
              city_id: data.city_id,
              number_of_cars: String(data.number_of_cars)
            });
          case 4:
            reset();
            resetCaptcha();
            successModal.setTrue();
            _context.n = 6;
            break;
          case 5:
            _context.p = 5;
            _t = _context.v;
            resetCaptcha();
            failModal.setTrue();
          case 6:
            _context.p = 6;
            submitting.setFalse();
            return _context.f(6);
          case 7:
            return _context.a(2);
        }
      }, _callee, null, [[3, 5, 6, 7]]);
    }));
    return function onSubmit(_x) {
      return _ref2.apply(this, arguments);
    };
  }();
  var cityList = cities !== null && cities !== void 0 ? cities : [];
  return /*#__PURE__*/React.createElement("section", {
    id: "fleet-request-form",
    className: "px-4 py-8 md:py-14 bg-white"
  }, /*#__PURE__*/React.createElement("div", {
    className: "md:max-w-[1200px] md:mx-auto flex flex-col md:flex-row gap-6 md:gap-8 md:items-stretch"
  }, /*#__PURE__*/React.createElement("div", {
    className: " my-4 hidden md:block md:w-[40%] rounded-2xl overflow-hidden order-2"
  }, /*#__PURE__*/React.createElement("img", {
    src: formImage,
    alt: tShort("form_title", {
      ns: "web_fleet"
    }),
    loading: "lazy",
    width: 1804,
    height: 2696,
    className: "w-full h-full object-cover"
  })), /*#__PURE__*/React.createElement("div", {
    className: "flex-1 bg-white shadow-xl md:shadow-none rounded-lg border border-solid border-[#E6E9EF] p-5 md:p-0 md:rounded-none md:border-0"
  }, /*#__PURE__*/React.createElement("h2", {
    className: "text-xl md:text-[28px] font-bold text-primary-new text-center md:text-start md:mb-5 mb-2"
  }, tShort("form_title", {
    ns: "web_fleet"
  })), /*#__PURE__*/React.createElement("p", {
    className: "text-sm md:text-base text-center md:text-start mb-6 md:mb-8"
  }, tShort("form_paragraph", {
    ns: "web_fleet"
  })), /*#__PURE__*/React.createElement("form", {
    onSubmit: handleSubmit(onSubmit),
    className: "bg-white md:rounded-2xl md:border md:border-solid md:border-[#E6E9EF] md:p-8",
    noValidate: true
  }, /*#__PURE__*/React.createElement("h3", {
    className: "hidden md:block text-lg font-bold text-[#1B2035] mb-6"
  }, tShort("form_personal_info", {
    ns: "web_fleet"
  })), /*#__PURE__*/React.createElement("div", {
    className: "grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-5"
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(FieldLabel, {
    required: true
  }, tShort("field_name", {
    ns: "web_fleet"
  })), /*#__PURE__*/React.createElement(Controller, {
    name: "first_name",
    control: control,
    render: function render(_ref3) {
      var field = _ref3.field,
        error = _ref3.fieldState.error;
      return /*#__PURE__*/React.createElement(Input, _extends({}, field, {
        id: "fleet-name",
        type: "text",
        placeholder: tShort("field_name_placeholder", {
          ns: "web_fleet"
        }),
        error: error ? tShort(error.message, {
          ns: "validations"
        }) : ""
      }));
    }
  })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(FieldLabel, {
    required: true
  }, tShort("field_company", {
    ns: "web_fleet"
  })), /*#__PURE__*/React.createElement(Controller, {
    name: "organization",
    control: control,
    render: function render(_ref4) {
      var field = _ref4.field,
        error = _ref4.fieldState.error;
      return /*#__PURE__*/React.createElement(Input, _extends({}, field, {
        id: "fleet-company",
        type: "text",
        placeholder: tShort("field_company_placeholder", {
          ns: "web_fleet"
        }),
        error: error ? tShort(error.message, {
          ns: "validations"
        }) : ""
      }));
    }
  })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(FieldLabel, {
    required: true
  }, tShort("field_phone", {
    ns: "web_fleet"
  })), /*#__PURE__*/React.createElement(Controller, {
    name: "mobile_no",
    control: control,
    render: function render(_ref5) {
      var _ref5$field = _ref5.field,
        onChange = _ref5$field.onChange,
        value = _ref5$field.value,
        onBlur = _ref5$field.onBlur,
        error = _ref5.fieldState.error;
      return /*#__PURE__*/React.createElement(InputPhoneNumber, {
        id: "fleet-phone",
        haveLabel: false,
        defaultValue: value,
        onChange: onChange,
        onBlur: onBlur,
        placeholder: "05xxxxxxxx",
        errorLabel: error ? tShort(error.message, {
          ns: "validations"
        }) : "",
        errorLabelClassName: ""
      });
    }
  })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(FieldLabel, null, tShort("field_email", {
    ns: "web_fleet"
  })), /*#__PURE__*/React.createElement(Controller, {
    name: "email",
    control: control,
    render: function render(_ref6) {
      var field = _ref6.field,
        error = _ref6.fieldState.error;
      return /*#__PURE__*/React.createElement(Input, _extends({}, field, {
        id: "fleet-email",
        type: "text",
        placeholder: tShort("field_email_placeholder", {
          ns: "web_fleet"
        }),
        error: error ? tShort(error.message, {
          ns: "validations"
        }) : ""
      }));
    }
  })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(FieldLabel, {
    required: true
  }, tShort("field_city", {
    ns: "web_fleet"
  })), /*#__PURE__*/React.createElement(Controller, {
    name: "city_id",
    control: control,
    render: function render(_ref7) {
      var field = _ref7.field,
        error = _ref7.fieldState.error;
      return /*#__PURE__*/React.createElement(Select, {
        id: "fleet-city",
        name: "city_id",
        value: field.value,
        renderEmptyOption: false,
        onChange: function onChange(e) {
          return field.onChange(e.target.value);
        },
        error: error ? tShort(error.message, {
          ns: "validations"
        }) : ""
      }, /*#__PURE__*/React.createElement("option", {
        value: "",
        disabled: true,
        hidden: true
      }, tShort("field_city_placeholder", {
        ns: "web_fleet"
      })), cityList.map(function (city) {
        return /*#__PURE__*/React.createElement("option", {
          key: city.id,
          value: city.id
        }, city.name);
      }));
    }
  })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(FieldLabel, {
    required: true
  }, tShort("field_number_of_cars", {
    ns: "web_fleet"
  })), /*#__PURE__*/React.createElement(Controller, {
    name: "number_of_cars",
    control: control,
    render: function render(_ref8) {
      var field = _ref8.field,
        error = _ref8.fieldState.error;
      return /*#__PURE__*/React.createElement(NumberStepper, {
        id: "fleet-number-of-cars",
        value: field.value,
        onChange: field.onChange,
        error: error ? tShort(error.message, {
          ns: "validations"
        }) : ""
      });
    }
  })), /*#__PURE__*/React.createElement("div", {
    className: "md:col-span-2"
  }, /*#__PURE__*/React.createElement(FieldLabel, {
    required: true
  }, tShort("field_description", {
    ns: "web_fleet"
  })), /*#__PURE__*/React.createElement(Controller, {
    name: "request",
    control: control,
    render: function render(_ref9) {
      var field = _ref9.field,
        error = _ref9.fieldState.error;
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("textarea", _extends({}, field, {
        id: "fleet-description",
        rows: 4,
        placeholder: tShort("field_description_placeholder", {
          ns: "web_fleet"
        }),
        className: "w-full rounded-lg border border-solid p-3 text-sm text-[#1B2035] outline-none resize-y placeholder:text-[#9AA7C2] ".concat(error ? "border-[#E4002B]" : "border-[#E6E9EF]")
      })), error && /*#__PURE__*/React.createElement("span", {
        className: "block mt-1 text-xs text-[#E4002B]"
      }, tShort(error.message, {
        ns: "validations"
      })));
    }
  }))), /*#__PURE__*/React.createElement("div", {
    className: "mt-6"
  }, /*#__PURE__*/React.createElement(ReCaptcha, {
    key: captchaKey,
    lang: lang === "ar" ? "ar" : "en",
    onChange: function onChange(token) {
      setCaptchaToken(token);
      if (token) captchaInvalid.setFalse();
    }
  }), captchaInvalid.value && /*#__PURE__*/React.createElement("span", {
    className: "block mt-1 text-xs text-[#E4002B]"
  }, tShort("captcha_required", {
    ns: "validations"
  }))), /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col items-center md:items-start mt-8"
  }, /*#__PURE__*/React.createElement("button", {
    type: "submit",
    disabled: submitting.value,
    className: "text-center w-full md:w-auto px-8 py-3 bg-primary-new text-white border-none rounded-lg text-base font-semibold cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
  }, tShort("submit", {
    ns: "web_fleet"
  })), /*#__PURE__*/React.createElement("span", {
    className: "mt-8 mb-6 text-base text-primary-new text-center md:text-end md:hidden"
  }, tShort("form_note", {
    ns: "web_fleet"
  })))))), /*#__PURE__*/React.createElement(FleetResultModal, {
    open: successModal.value,
    variant: "success",
    onClose: successModal.setFalse,
    onRetry: successModal.setFalse
  }), /*#__PURE__*/React.createElement(FleetResultModal, {
    open: failModal.value,
    variant: "fail",
    onClose: failModal.setFalse,
    onRetry: failModal.setFalse
  }));
};
export default FleetForm;